import { useCallback, useMemo } from 'react';
import type { CustomerAccountRow } from '../../domain/customerTypes';
import type { PaginatedListPageSize } from '../../../shared/data/usePaginatedRemoteList';
import { customersScreenStyles as styles } from '../customersScreen.styles';

type Params = {
  canEdit: boolean;
  customers: CustomerAccountRow[];
  customerPageIndex: number;
  customerPageSize: PaginatedListPageSize;
  setCustomerPageSize: (value: PaginatedListPageSize) => void;
  customerHasMore: boolean;
  customerLoading: boolean;
  customerResultTotalCount: number;
  loadCustomerPreviousPage: () => void;
  loadCustomerNextPage: () => void;
  onAddCustomer: () => void;
  /** Error de la última carga remota (null = ok). Sin esto la pantalla mentía "Todavía no hay clientes". */
  customerError?: string | null;
  /** Reintento del fetch de lista (se muestra cuando hay `customerError`). */
  onRetryLoad?: () => void;
  /** Status/util/date filters or non-empty search (sort alone does not count). */
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
};

export function useCustomerListPane({
  canEdit,
  customers,
  customerPageIndex,
  customerPageSize,
  setCustomerPageSize,
  customerHasMore,
  customerLoading,
  customerResultTotalCount,
  loadCustomerPreviousPage,
  loadCustomerNextPage,
  onAddCustomer,
  customerError = null,
  onRetryLoad,
  hasActiveFilters = false,
  onClearFilters,
}: Params) {
  const paginationProps = {
    catalogPageIndex: customerPageIndex,
    pageSize: customerPageSize,
    catalogTotalCount: customerResultTotalCount,
    hasMore: customerHasMore,
    productsLoading: customerLoading,
    productCount: customers.length,
    onPreviousPage: loadCustomerPreviousPage,
    onNextPage: loadCustomerNextPage,
    listNouns: { singular: 'cliente', plural: 'clientes' } as const,
    alwaysShowPagination: true as const,
    pageSizePicker: {
      label: 'Clientes por página',
      options: [5, 10] as const,
      value: (customerPageSize <= 10 ? customerPageSize : 10) as 5 | 10,
      onChange: setCustomerPageSize,
      accessibilityLabelPrefix: 'Clientes por página',
    },
  };

  const listEmptyProps = customerError && !customerLoading
    ? {
        loading: false,
        loadingText: 'Cargando clientes…',
        title: 'No se pudieron cargar los clientes',
        subtitle: 'Revisá tu conexión y reintentá.',
        secondaryAction: onRetryLoad
          ? ({ label: 'Reintentar', onPress: onRetryLoad } as const)
          : undefined,
        testID: 'customers.list.error',
      }
    : hasActiveFilters
      ? {
          loading: customerLoading,
          loadingText: 'Cargando clientes…',
          title: 'Sin resultados',
          subtitle: 'Nadie coincide con eso. Probá otra búsqueda o sacá los filtros.',
          secondaryAction: onClearFilters
            ? ({ label: 'Limpiar filtros', onPress: onClearFilters } as const)
            : undefined,
        }
      : {
          loading: customerLoading,
          loadingText: 'Cargando clientes…',
          title: 'Todavía no hay clientes',
          subtitle: canEdit
            ? 'Agregá tu primer cliente.'
            : 'Cuando el negocio cargue clientes, aparecen acá. Necesitás permiso de administrador para agregar uno.',
          primaryAction: canEdit ? ({ label: 'Agregar cliente', onPress: onAddCustomer } as const) : undefined,
        };

  const customerListContentStyle = useMemo(
    () => [styles.listContent, customers.length === 0 && styles.listContentEmpty],
    [customers.length]
  );

  const customerKeyExtractor = useCallback((item: CustomerAccountRow) => item.id, []);

  return {
    paginationProps,
    listEmptyProps,
    customerListContentStyle,
    customerKeyExtractor,
  };
}
